import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon, CogIcon, CheckCircleIcon, ExclamationTriangleIcon,
  ArrowPathIcon, DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const SettingsBulkUpdate = ({ settings, onClose, onUpdate }) => {
  const [selectedSettings, setSelectedSettings] = useState([]);
  const [updates, setUpdates] = useState({});
  const [loading, setLoading] = useState(false);

  const handleSettingSelect = (setting) => {
    if (selectedSettings.find(s => s.id === setting.id)) {
      setSelectedSettings(selectedSettings.filter(s => s.id !== setting.id));
      const newUpdates = { ...updates };
      delete newUpdates[setting.key];
      setUpdates(newUpdates);
    } else {
      setSelectedSettings([...selectedSettings, setting]);
      setUpdates({
        ...updates,
        [setting.key]: setting.value
      });
    }
  };

  const handleValueChange = (key, value) => {
    setUpdates({
      ...updates,
      [key]: value
    });
  };

  const handleSubmit = async () => {
    if (selectedSettings.length === 0) {
      toast.error('Please select at least one setting to update');
      return;
    }

    const updatesArray = Object.entries(updates).map(([key, value]) => ({
      key,
      value
    }));

    try {
      setLoading(true);
      await onUpdate(updatesArray);
      onClose();
    } catch (error) {
      console.error('Error bulk updating settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getValueTypeInput = (setting) => {
    const value = updates[setting.key] || setting.value;

    switch (setting.valueType) {
      case 'boolean':
        return (
          <select
            value={value}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );

      case 'json':
        return (
          <textarea
            value={value}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
          />
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleValueChange(setting.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );
    }
  };

  const isSettingSelected = (setting) => {
    return selectedSettings.find(s => s.id === setting.id);
  };

  const isSettingChanged = (setting) => {
    const currentValue = updates[setting.key];
    return currentValue !== undefined && currentValue !== setting.value;
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CogIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div className="ml-3">
                <h2 className="text-lg font-semibold text-gray-900">Bulk Update Settings</h2>
                <p className="text-sm text-gray-600">
                  Update multiple settings at once
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Instructions */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                <div>
                  <h3 className="text-sm font-medium text-blue-900">How to use bulk update</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Select the settings you want to update, modify their values, and click "Update Settings" to apply all changes at once.
                  </p>
                </div>
              </div>
            </div>

            {/* Settings List */}
            <div className="space-y-4">
              {settings.map((setting) => {
                const isSelected = isSettingSelected(setting);
                const isChanged = isSettingChanged(setting);
                
                return (
                  <motion.div
                    key={setting.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`border rounded-lg p-4 transition-all ${
                      isSelected 
                        ? 'border-blue-300 bg-blue-50' 
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      {/* Checkbox */}
                      <div className="flex-shrink-0 pt-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSettingSelect(setting)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      </div>

                      {/* Setting Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">
                              {setting.displayName || setting.key}
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                              {setting.key}
                            </p>
                            {setting.description && (
                              <p className="text-xs text-gray-600 mt-1">
                                {setting.description}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              setting.isSystem 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {setting.category}
                            </span>
                            {isChanged && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                Modified
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Current Value */}
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Current Value
                          </label>
                          <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded border">
                            {setting.valueType === 'boolean' ? (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                setting.value === 'true' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {setting.value === 'true' ? 'True' : 'False'}
                              </span>
                            ) : (
                              <span className="font-mono">
                                {String(setting.value).length > 50 
                                  ? `${String(setting.value).substring(0, 50)}...` 
                                  : setting.value
                                }
                              </span>
                            )}
                          </div>
                        </div>

                        {/* New Value Input */}
                        {isSelected && (
                          <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              New Value
                            </label>
                            {getValueTypeInput(setting)}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Summary */}
            {selectedSettings.length > 0 && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedSettings.length} setting{selectedSettings.length !== 1 ? 's' : ''} selected
                    </p>
                    <p className="text-sm text-gray-600">
                      {Object.keys(updates).filter(key => 
                        selectedSettings.find(s => s.key === key && s.value !== updates[key])
                      ).length} setting{Object.keys(updates).filter(key => 
                        selectedSettings.find(s => s.key === key && s.value !== updates[key])
                      ).length !== 1 ? 's' : ''} will be modified
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setSelectedSettings([]);
                        setUpdates({});
                      }}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || selectedSettings.length === 0}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4 mr-2" />
                  Update Settings ({selectedSettings.length})
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SettingsBulkUpdate; 